import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

import { AssistPropertyBM, LocalizedString } from '@cue/api';
import { NavigationDetailInfo, NavigationService, QrIdListPipe } from '@cue/navigation-services';
import { NAVIGATION_SETTINGS } from '@cue/navigation-settings';
import { UnitDetailComponent } from '@cue/navigation-widgets';
import { CustomUnsafeSanitizePipe } from '@cue/security';
import { localized } from '@cue/translate';
import { translation } from '@cue/translations';
import { ControllableObjectsComponent } from '@cue/ui';
import { FileGalleryComponent } from '@cue/ui/file-gallery';
import { cueArrowRight, cueCheck, cueClose, IconComponent } from '@cue/ui/icons';
import { ImageWidthPipe, LocalizedStringPipe } from '@cue/utilities';

@Component({
  selector: 'assist-multiview-renew-detail-qr',
  imports: [
    ControllableObjectsComponent,
    CustomUnsafeSanitizePipe,
    DatePipe,
    FileGalleryComponent,
    IconComponent,
    ImageWidthPipe,
    LocalizedStringPipe,
    QrIdListPipe,
    TranslocoPipe,
    UnitDetailComponent,
  ],
  templateUrl: './multiview-renew-detail-qr.component.html',
  styleUrl: './multiview-renew-detail-qr.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiviewRenewDetailQrComponent {
  protected readonly ICONS = {
    cueArrowRight,
    cueCheck,
    cueClose,
  };
  protected readonly translation = translation;
  protected readonly navigationSettings = inject(NAVIGATION_SETTINGS);
  private readonly translocoService = inject(TranslocoService);
  private readonly navigationService = inject(NavigationService);

  readonly item = input.required<NavigationDetailInfo>();

  private readonly scheduleDetail = rxResource({
    params: () => this.item().resourceId ?? undefined,
    stream: ({ params }) =>
      this.navigationService.getNavigationSchedule({
        resourceId: params,
        appId: null,
        kioskId: null,
      }),
  });

  protected readonly schedule = computed(() => this.scheduleDetail.value() ?? this.item().schedule ?? null);
  protected readonly availability = computed(() => this.schedule()?.availabilityView?.[0] ?? null);

  protected readonly availabilityText = computed(() => {
    const availability = this.availability();
    return availability == null ? null : this.availabilityTextMap[availability];
  });

  protected readonly availabilityColor = computed(() => {
    const availability = this.availability();
    if (availability == null) {
      return {};
    }

    let color = '';
    switch (availability) {
      case '0':
        color = this.navigationSettings.design.background.reservableResource.free;
        break;
      case '1':
        color = this.navigationSettings.design.background.reservableResource.tentative;
        break;
      case '2':
      case '9':
        color = this.navigationSettings.design.background.reservableResource.reserved;
        break;
      case '8':
        color = this.navigationSettings.design.background.reservableResource.freeClosed;
        break;
      default:
        color = this.navigationSettings.design.background.reservableResource.closed;
        break;
    }

    return {
      backgroundColor: this.removeAlphaFromRgba(color),
      color: this.getTextColorBasedOnBg(color),
    };
  });

  protected readonly itemName = computed(() => localized(this.item().name, this.translocoService));

  protected readonly parentResourceName = computed(
    () =>
      localized(this.item().displayNameForApp, this.translocoService) ||
      localized(this.item().name, this.translocoService)
  );

  protected readonly simpleFilters = computed(() =>
    this.getFiltersFromNavigationDetail(this.item()).filter((filter) => filter.dataTypeId !== 5)
  );

  protected readonly enumerableFilters = computed(() =>
    this.getFiltersFromNavigationDetail(this.item()).filter(
      (filter) => filter.dataTypeId === 5 && filter.value?.length > 0
    )
  );

  private readonly availabilityTextMap: Record<string, string> = {
    '0': translation.availability.free,
    '1': translation.availability.tentative,
    '2': translation.availability.reserved,
    '3': translation.availability.notReservableNow,
    '4': translation.availability.notReservableNow,
    '5': translation.availability.notReservableNow,
    '6': translation.availability.notReservableNow,
    '7': translation.availability.notReservableNow,
    '8': translation.availability.freeClosed,
    '9': translation.availability.reserved,
  };

  private getFiltersFromNavigationDetail(
    detail: NavigationDetailInfo
  ): { name: LocalizedString; value: any; dataTypeId: number; visible: boolean }[] {
    return (detail.properties ?? [])
      .map((property) => ({
        name: property.name,
        visible: property.visible,
        dataTypeId: property.propertyTypeId,
        value: this.getValueFromProperty(property),
      }))
      .filter((filter) => filter.value != null);
  }

  private getValueFromProperty(property: AssistPropertyBM & { propertyTypeId: number; valueChoices: any[] }): any {
    switch (property.propertyTypeId) {
      case 1:
        return { value: property.valueBoolean, imageUrl: property.file };
      case 2:
        return { value: property.valueNumber, imageUrl: property.file };
      case 3:
        return { value: property.valueDecimal, imageUrl: property.file };
      case 4:
        return { value: property.valueText, imageUrl: property.file };
      case 5:
        return (property.valueChoices ?? []).map((choice) => ({
          title: choice.value,
          imageUrl: choice.imageUrl,
        }));
      case 6:
        return { value: property.valueHTMLContent, imageUrl: property.file };
      default:
        return null;
    }
  }

  private getTextColorBasedOnBg(color?: string): string {
    if (!color) {
      return '#212529';
    }

    const [red, green, blue] = (color.match(/\d+/g) ?? []).map(Number);
    if (red === undefined || green === undefined || blue === undefined) {
      return '#212529';
    }

    return 0.2126 * red + 0.7152 * green + 0.0722 * blue > 140 ? '#212529' : '#fff';
  }

  private removeAlphaFromRgba(color: string): string {
    const match = color.match(/^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*[\d.]+\)$/);
    return match ? `rgb(${match[1]}, ${match[2]}, ${match[3]})` : color;
  }
}
